// What the caller has open, for Courtney's getPageInfo RPC.
//
// The host app writes `window.__courtsapp_page` (web widget, same window) or
// the native WebView bridge calls rememberHostPage (the /mobile embed does
// not share the app's window). Field allowlisting happens in the agent
// (tools/page_info.py) — this only picks which snapshot to send and strips
// the query string so tokens in the URL never leave the browser.

export const PAGE_INFO_METHOD = "getPageInfo";

export type PageKind = "facility" | "page" | "unknown";

export type PageInfo = {
  kind: PageKind;
  path: string;
  title?: string;
  facility?: Record<string, unknown>;
};

type PageEnv = {
  host: unknown;
  path: string;
  title: string;
  inWebView: boolean;
};

declare global {
  interface Window {
    __courtsapp_page?: unknown;
    ReactNativeWebView?: { postMessage: (msg: string) => void };
  }
}

function field(record: object, key: string): unknown {
  if (!(key in record)) {
    return undefined;
  }
  return Reflect.get(record, key);
}

function cleanPath(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const path = value.trim().split("?")[0]?.split("#")[0] ?? "";
  if (!path.startsWith("/") || path.length > 300 || /\s/.test(path)) {
    return undefined;
  }
  return path;
}

function facilityRecord(value: unknown): Record<string, unknown> | undefined {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(value)) {
    if (key.length > 40) {
      continue;
    }
    const item = field(value, key);
    if (typeof item === "string") {
      if (item.length > 0) out[key] = item.slice(0, 300);
    } else if (typeof item === "number" || typeof item === "boolean") {
      out[key] = item;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

// Pass the host snapshot through with the path's query stripped. Returns null
// when the host hasn't published a page yet.
export function readHostPage(host: unknown): PageInfo | null {
  if (host === null || typeof host !== "object" || Array.isArray(host)) {
    return null;
  }
  const kind = field(host, "kind");
  const path = cleanPath(field(host, "path")) ?? "";
  const titleValue = field(host, "title");
  const title =
    typeof titleValue === "string" && titleValue.trim()
      ? titleValue.trim().slice(0, 200)
      : undefined;
  if (kind === "unknown") {
    return title ? { kind, path, title } : { kind, path };
  }
  if (kind !== "facility" && kind !== "page") {
    return null;
  }
  if (kind === "page" && !path) {
    return null;
  }
  const page: PageInfo = title ? { kind, path, title } : { kind, path };
  if (kind === "facility") {
    const facility = facilityRecord(field(host, "facility"));
    if (!facility) {
      return null;
    }
    page.facility = facility;
  }
  return page;
}

export function readPageInfo(env: PageEnv): PageInfo {
  const host = readHostPage(env.host);
  if (host) {
    return host;
  }
  if (env.inWebView) {
    return { kind: "unknown", path: "" };
  }
  const path = cleanPath(env.path) ?? "/";
  const title = env.title.trim().slice(0, 200);
  return title ? { kind: "page", path, title } : { kind: "page", path };
}

export function rememberHostPage(page: unknown): void {
  if (typeof window === "undefined") {
    return;
  }
  window.__courtsapp_page = page;
}

export function currentPageInfo(): PageInfo {
  return readPageInfo({
    host: typeof window === "undefined" ? undefined : window.__courtsapp_page,
    path: typeof location === "undefined" ? "" : location.pathname,
    title: typeof document === "undefined" ? "" : document.title,
    inWebView:
      typeof window !== "undefined" && window.ReactNativeWebView !== undefined,
  });
}
